import React, { lazy, ReactNode, Suspense, useCallback, useEffect } from 'react';

import { useAbTestCheckoutWithoutUpselling } from '@hooks';
import { EOrderAsLastTimeState } from '@model';
import { useTrackOrderAsLastTimePageViewEvent } from '@services/ecommerceService';
import { ModalErrorBoundary } from '@services/errorService';
import { useRedirect } from '@services/routingService';
import { Loader } from '@sharedComponents/Loader';

import { ORDER_AS_LAST_TIME_LOADER_SIZE } from '../components';
import { ECartPageState, useCartPageContext } from '../context';

const OrderAsLastTimeModalWrapper = lazy(
  () => import('../components/OrderAsLastTimeModalWrapper/OrderAsLastTimeModalWrapper'),
);

export const useOrderAsLastTimeModal = (
  onModalClose: () => void,
  actionComponent: ReactNode,
  pageState: EOrderAsLastTimeState,
  onContinueToCheckout: () => void,
  isOaltProcessing: boolean,
): JSX.Element => {
  const {
    state: { cartPageState, lastOrder, products, pricesSummary, serviceItems },
  } = useCartPageContext();

  const { redirectPage } = useAbTestCheckoutWithoutUpselling();
  const redirect = useRedirect();

  const onUpdateDetail = useCallback(() => {
    onModalClose();
    redirect(redirectPage);
  }, [onModalClose, redirectPage, redirect]);

  const trackOrderAsLastTimePageView = useTrackOrderAsLastTimePageViewEvent();

  useEffect(() => {
    if (lastOrder && cartPageState === ECartPageState.ORDER_AS_LAST_TIME) {
      trackOrderAsLastTimePageView();
    }
  }, [cartPageState, lastOrder, trackOrderAsLastTimePageView]);

  return (
    <ModalErrorBoundary onError={onModalClose}>
      <Suspense fallback={<Loader height={ORDER_AS_LAST_TIME_LOADER_SIZE} />}>
        <OrderAsLastTimeModalWrapper
          lastOrder={lastOrder}
          onUpdateDetail={onUpdateDetail}
          products={products}
          serviceItems={serviceItems}
          pricesSummary={pricesSummary}
          actionComponent={actionComponent}
          pageState={pageState}
          onContinueToCheckout={onContinueToCheckout}
          isOaltProcessing={isOaltProcessing}
        />
      </Suspense>
    </ModalErrorBoundary>
  );
};
